import React, { useState, useEffect } from 'react';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';
import Modal from './Modal';

function normalizePhoneNumber(phone) {
    if (!phone) return '';
    const trimmed = phone.trim();
    if (!trimmed) return '';
    
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return trimmed; // fallback
    
    // US-based: 10 digits, or 11 digits starting with 1
    if ((digits.length === 10 && (!hasPlus || trimmed.startsWith('+1'))) ||
        (digits.length === 11 && digits.startsWith('1'))) {
        const area = digits.slice(-10, -7);
        const prefix = digits.slice(-7, -4);
        const line = digits.slice(-4);
        return `(${area}) ${prefix}-${line}`;
    }
    
    // International
    if (hasPlus || digits.length > 10) {
        let ccLen = 2;
        if (digits.startsWith('7')) {
            ccLen = 1;
        } else if (digits.length >= 2) {
            const firstTwo = digits.slice(0, 2);
            const twoDigitCCs = new Set([
                '20', '27', '30', '31', '32', '33', '34', '36', '39',
                '40', '41', '43', '44', '45', '46', '47', '48', '49',
                '51', '52', '53', '54', '55', '56', '57', '58',
                '60', '61', '62', '63', '64', '65', '66',
                '81', '82', '84', '86',
                '90', '91', '92', '93', '94', '95', '98'
            ]);
            if (!twoDigitCCs.has(firstTwo) && digits.length >= 3) {
                ccLen = 3;
            }
        }
        if (digits.length < ccLen) {
            return trimmed;
        }
        const cc = digits.slice(0, ccLen);
        const rest = digits.slice(ccLen);
        if (rest.length <= 3) {
            return `+${cc} ${rest}`;
        } else if (rest.length <= 7) {
            return `+${cc} ${rest.slice(0, 3)} ${rest.slice(3)}`;
        } else {
            return `+${cc} ${rest.slice(0, 3)} ${rest.slice(3, 7)}-${rest.slice(7)}`;
        }
    }
    
    return trimmed;
}

export default function EditContactModal({ isOpen, onClose, contactData, onSuccess }) {
    const { userEmail } = useAddin();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (contactData) {
            setFormData({
                first_name: contactData.first_name || '',
                middle_name: contactData.middle_name || '',
                last_name: contactData.last_name || '',
                prefix: contactData.prefix || '',
                title: contactData.title || '',
                initials: contactData.initials || '',
                email_addresses: contactData.email_addresses?.map(e => ({ ...e })) || [],
                phone_numbers: contactData.phone_numbers?.map(p => ({ ...p, number: normalizePhoneNumber(p.number) })) || [],
                addresses: contactData.addresses?.map(a => ({ ...a })) || [],
                web_sites: contactData.web_sites?.map(w => ({ ...w })) || [],
                company_name: contactData.company?.name || '',
                company_id: contactData.company?.id || null,
                date_of_birth: contactData.date_of_birth || '',
                sales_tax_number: contactData.sales_tax_number || '',
                ledes_client_id: contactData.ledes_client_id || '',
                clio_connect_email: contactData.clio_connect_email || '',
                is_client: !!contactData.is_client,
            });
        }
    }, [contactData]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleArrayChange = (arrayName, index, field, value) => {
        setFormData(prev => {
            const newArr = [...prev[arrayName]];
            newArr[index] = { ...newArr[index], [field]: value };
            
            if (field === 'primary' && value === true) {
                for (let i = 0; i < newArr.length; i++) {
                    if (i !== index) newArr[i].primary = false;
                }
            }
            return { ...prev, [arrayName]: newArr };
        });
    };

    const addArrayItem = (arrayName, defaultItem) => {
        setFormData(prev => ({ ...prev, [arrayName]: [...prev[arrayName], defaultItem] }));
    };

    const removeArrayItem = (arrayName, index) => {
        setFormData(prev => {
            const newArr = [...prev[arrayName]];
            if (newArr[index].id) {
                newArr[index]._destroy = true;
            } else {
                newArr.splice(index, 1);
            }
            return { ...prev, [arrayName]: newArr };
        });
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            const payload = { ...formData };
            if (payload.phone_numbers) {
                payload.phone_numbers = payload.phone_numbers.map(p => ({
                    ...p,
                    number: normalizePhoneNumber(p.number)
                }));
            }
            if (payload.company_id) {
                payload.company = { id: payload.company_id };
            } else if (payload.company_name) {
                payload.company = null; // Can't easily link dynamically here without search; we could pass company_name though.
            }
            
            const reqData = {
                userEmail,
                contactId: contactData.id,
                etag: contactData.etag,
                contactData: payload
            };

            const result = await webhook.updateContact(reqData);
            setLoading(false);
            if (onSuccess) onSuccess(result.data || result.contact || result);
        } catch (err) {
            console.error("Failed to update contact:", err);
            setError(err.message);
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Edit Contact">
            <div className="addin-body" style={{ padding: '0', overflowY: 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px' }}>
                    <div className="section-label" style={{ marginTop: 0 }}>Basic Info</div>

                    <div className="form-group">
                        <label className="form-label">First Name</label>
                        <input type="text" className="form-input" value={formData.first_name || ''} onChange={e => handleChange('first_name', e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Middle Name</label>
                        <input type="text" className="form-input" value={formData.middle_name || ''} onChange={e => handleChange('middle_name', e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Last Name</label>
                        <input type="text" className="form-input" value={formData.last_name || ''} onChange={e => handleChange('last_name', e.target.value)} />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Title</label>
                        <input type="text" className="form-input" value={formData.title || ''} onChange={e => handleChange('title', e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Prefix / Initials</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input type="text" className="form-input" style={{flex: 1}} placeholder="Prefix" value={formData.prefix || ''} onChange={e => handleChange('prefix', e.target.value)} />
                            <input type="text" className="form-input" style={{flex: 1}} placeholder="Initials" value={formData.initials || ''} onChange={e => handleChange('initials', e.target.value)} />
                        </div>
                    </div>

                    <div className="section-label">Additional Info</div>
                    <div className="form-group">
                        <label className="form-label">Company</label>
                        <input type="text" className="form-input" value={formData.company_name || ''} onChange={e => handleChange('company_name', e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Date of Birth</label>
                        <input type="date" className="form-input" value={formData.date_of_birth || ''} onChange={e => handleChange('date_of_birth', e.target.value)} />
                    </div>

                    {/* Emails Array */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Email Addresses</span>
                            <button className="text-secondary" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: 'var(--teal)' }} onClick={() => addArrayItem('email_addresses', { address: '', name: 'Work', primary: false })}>+ Add</button>
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {formData.email_addresses?.map((email, i) => {
                                if (email._destroy) return null;
                                return (
                                <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <select className="form-input" style={{ width: '80px', padding: '4px' }} value={email.name || 'Work'} onChange={(e) => handleArrayChange('email_addresses', i, 'name', e.target.value)}>
                                        <option value="Work">Work</option>
                                        <option value="Home">Home</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    <input className="form-input" style={{ flex: 1 }} type="email" value={email.address || ''} onChange={(e) => handleArrayChange('email_addresses', i, 'address', e.target.value)} placeholder="email@example.com" />
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                        <input type="radio" name={`primary_email_${i}`} checked={!!email.primary} onChange={() => handleArrayChange('email_addresses', i, 'primary', true)} style={{ accentColor: 'var(--teal)', cursor: 'pointer' }} /> Primary
                                    </label>
                                    <button style={{ background: 'none', border: 'none', color: 'var(--grey02)', cursor: 'pointer', fontSize: '12px', padding: '0 4px' }} onClick={() => removeArrayItem('email_addresses', i)}>✕</button>
                                </div>
                            )})}
                        </div>
                    </div>

                    {/* Phones Array */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Phone Numbers</span>
                            <button className="text-secondary" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: 'var(--teal)' }} onClick={() => addArrayItem('phone_numbers', { number: '', name: 'Work', primary: false })}>+ Add</button>
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {formData.phone_numbers?.map((phone, i) => {
                                if (phone._destroy) return null;
                                return (
                                <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <select className="form-input" style={{ width: '80px', padding: '4px' }} value={phone.name || 'Work'} onChange={(e) => handleArrayChange('phone_numbers', i, 'name', e.target.value)}>
                                        <option value="Work">Work</option>
                                        <option value="Mobile">Mobile</option>
                                        <option value="Home">Home</option>
                                        <option value="Fax">Fax</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    <input
                                        className="form-input"
                                        style={{ flex: 1 }}
                                        value={phone.number || ''}
                                        onChange={(e) => handleArrayChange('phone_numbers', i, 'number', e.target.value)}
                                        onBlur={(e) => handleArrayChange('phone_numbers', i, 'number', normalizePhoneNumber(e.target.value))}
                                        placeholder="+1 555 000 0000"
                                    />
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                        <input type="radio" name={`primary_number_${i}`} checked={!!phone.primary} onChange={() => handleArrayChange('phone_numbers', i, 'primary', true)} style={{ accentColor: 'var(--teal)', cursor: 'pointer' }} /> Primary
                                    </label>
                                    <button style={{ background: 'none', border: 'none', color: 'var(--grey02)', cursor: 'pointer', fontSize: '12px', padding: '0 4px' }} onClick={() => removeArrayItem('phone_numbers', i)}>✕</button>
                                </div>
                            )})}
                        </div>
                    </div>

                    {/* Websites Array */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Websites</span>
                            <button className="text-secondary" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: 'var(--teal)' }} onClick={() => addArrayItem('web_sites', { address: '', name: 'Work', primary: false })}>+ Add</button>
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {formData.web_sites?.map((web, i) => {
                                if (web._destroy) return null;
                                return (
                                <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <select className="form-input" style={{ width: '80px', padding: '4px' }} value={web.name || 'Work'} onChange={(e) => handleArrayChange('web_sites', i, 'name', e.target.value)}>
                                        <option value="Work">Work</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    <input className="form-input" style={{ flex: 1 }} value={web.address || ''} onChange={(e) => handleArrayChange('web_sites', i, 'address', e.target.value)} placeholder="https://example.com" />
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                        <input type="radio" name={`primary_web_site_${i}`} checked={!!web.primary} onChange={() => handleArrayChange('web_sites', i, 'primary', true)} style={{ accentColor: 'var(--teal)', cursor: 'pointer' }} /> Primary
                                    </label>
                                    <button style={{ background: 'none', border: 'none', color: 'var(--grey02)', cursor: 'pointer', fontSize: '12px', padding: '0 4px' }} onClick={() => removeArrayItem('web_sites', i)}>✕</button>
                                </div>
                            )})}
                        </div>
                    </div>

                    {/* Addresses Array */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Addresses</span>
                            <button className="text-secondary" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: 'var(--teal)' }} onClick={() => addArrayItem('addresses', { street: '', city: '', province: '', postal_code: '', country: '', name: 'Work', primary: false })}>+ Add</button>
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {formData.addresses?.map((addr, i) => {
                                if (addr._destroy) return null;
                                return (
                                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                        <select className="form-input" style={{ width: '80px', padding: '4px' }} value={addr.name || 'Work'} onChange={(e) => handleArrayChange('addresses', i, 'name', e.target.value)}>
                                            <option value="Work">Work</option>
                                            <option value="Home">Home</option>
                                            <option value="Billing">Billing</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        <input className="form-input" style={{ flex: 1 }} value={addr.street || ''} onChange={(e) => handleArrayChange('addresses', i, 'street', e.target.value)} placeholder="Street Address" />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                            <input type="radio" name={`primary_address_${i}`} checked={!!addr.primary} onChange={() => handleArrayChange('addresses', i, 'primary', true)} style={{ accentColor: 'var(--teal)', cursor: 'pointer' }} /> Primary
                                        </label>
                                        <button style={{ background: 'none', border: 'none', color: 'var(--grey02)', cursor: 'pointer', fontSize: '12px', padding: '0 4px' }} onClick={() => removeArrayItem('addresses', i)}>✕</button>
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <input className="form-input" style={{ flex: 2 }} value={addr.city || ''} onChange={(e) => handleArrayChange('addresses', i, 'city', e.target.value)} placeholder="City" />
                                        <input className="form-input" style={{ flex: 1 }} value={addr.province || ''} onChange={(e) => handleArrayChange('addresses', i, 'province', e.target.value)} placeholder="State/Prov" />
                                        <input className="form-input" style={{ flex: 1 }} value={addr.postal_code || ''} onChange={(e) => handleArrayChange('addresses', i, 'postal_code', e.target.value)} placeholder="Zip/Postal" />
                                    </div>
                                    <input className="form-input" value={addr.country || ''} onChange={(e) => handleArrayChange('addresses', i, 'country', e.target.value)} placeholder="Country" />
                                </div>
                            )})}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                        <button className="secondary-btn" onClick={onClose} disabled={loading}>Cancel</button>
                        <button className="primary-btn" onClick={handleSave} disabled={loading}>
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
